import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { learnArticlePath } from '@/features/aprender/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'movil-hosteleria-falta-leve-boe'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    alehMod: {
        href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2026-18630',
        domain: 'boe.es',
        label: 'BOE · ALEH VI modificación',
    },
    alehVi: {
        href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2023-6344',
        domain: 'boe.es',
        label: 'BOE · ALEH VI',
    },
    et: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2015-11430',
        domain: 'boe.es',
        label: 'BOE · Estatuto de los Trabajadores',
    },
    smi: {
        href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2026-3815',
        domain: 'boe.es',
        label: 'BOE · SMI 2026',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

function BeneficioRealLink({ children }: { children: ReactNode }) {
    return (
        <LocaleLink to={learnArticlePath('beneficio-real-restaurante-cuanto-puedo-sacar')} className={article.link}>
            {children}
        </LocaleLink>
    )
}

export function MovilHosteleriaFaltaLeveArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'por-fin', label: t(k('toc.porFin'), 'Por fin algo que me deja decirle que suelte el móvil') },
        { id: 'boe', label: t(k('toc.boe'), 'Lo que el BOE ha escrito, palabra por palabra') },
        { id: 'falta-leve', label: t(k('toc.faltaLeve'), 'Falta leve significa dos días, y ni un euro') },
        { id: 'diez-dias', label: t(k('toc.diezDias'), 'Diez días. Ese es todo el tiempo que tienes') },
        {
            id: 'autorizacion',
            label: t(k('toc.autorizacion'), '"Salvo autorización de la empresa": esa frase la escribes tú'),
        },
        { id: 'coste', label: t(k('toc.coste'), 'Lo que cuesta de verdad castigar a un camarero dos días') },
        { id: 'cambio-gordo', label: t(k('toc.cambioGordo'), 'El cambio gordo no es el móvil') },
        { id: 'hoja', label: t(k('toc.hoja'), 'La hoja que te falta, y en qué orden escribirla') },
        { id: 'limites', label: t(k('toc.limites'), 'Lo que el texto no cierra') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del móvil como falta leve en hostelería y me gustaría ver cuánto me cuesta de verdad un día de personal.',
        ),
    )

    const policySteps: ReactNode[] = [
        <>
            <strong>
                {t(
                    k('hoja.1.strong'),
                    'Escribe la política de móvil en media página: qué está autorizado, para quién, en qué momentos y qué cuenta como emergencia.',
                )}
            </strong>{' '}
            {t(k('hoja.1.after'), 'Sin esto, lo demás no se sostiene.')}
        </>,
        <>
            <strong>
                {t(k('hoja.2.strong'), 'Repasa tu propio uso del móvil en el servicio antes de publicarla.')}
            </strong>{' '}
            {t(k('hoja.2.after'), 'Si los turnos salen de tu WhatsApp, eso va dentro de lo autorizado.')}
        </>,
        <>
            <strong>{t(k('hoja.3.strong'), 'Comunícala a toda la plantilla y guarda el acuse.')}</strong>{' '}
            {t(k('hoja.3.after'), 'El grupo sirve de prueba, la firma en papel sirve mejor.')}
        </>,
        <>
            <strong>
                {t(
                    k('hoja.4.strong'),
                    'Si llega el día de sancionar, por escrito, con fecha y hechos concretos, y dentro de los diez días.',
                )}
            </strong>
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'BOE-A-2026-18630. Resolución de 25 de agosto de 2026, de la Dirección General de Trabajo, por la que se registra y publica el Acuerdo de modificación del VI Acuerdo Laboral para el sector de la Hostelería, ALEH VI. Publicado el 4 de septiembre de 2026.',
            ),
            href: REF.alehMod.href,
        },
        {
            label: t(
                k('sources.2'),
                'BOE-A-2023-6344. VI Acuerdo Laboral de ámbito estatal para el sector de la Hostelería, ALEH VI, texto de origen con el cuadro de sanciones, la prescripción y la reserva del régimen disciplinario al ámbito estatal.',
            ),
            href: REF.alehVi.href,
        },
        {
            label: t(
                k('sources.3'),
                'Real Decreto Legislativo 2/2015, Estatuto de los Trabajadores, artículos 58 y 60.2.',
            ),
            href: REF.et.href,
        },
        {
            label: t(
                k('sources.4'),
                'Real Decreto 126/2026, de 18 de febrero, por el que se fija el salario mínimo interprofesional para 2026.',
            ),
            href: REF.smi.href,
        },
    ]

    const sectorSources = t(
        k('sources.5'),
        'Detalle del cuadro de sanciones y de la audiencia previa contrastado además con publicaciones sectoriales y de despachos laboralistas: Hostelería Madrid, Autónomos y Emprendedor e Ineaf.',
    )
    const ownSource = t(k('sources.6'), 'Cálculo del coste de la suspensión: elaboración propia de Tipi.')

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before'),
                                'El BOE publicó el 4 de septiembre de 2026 la modificación del VI Acuerdo Laboral Estatal de Hostelería. Coger el móvil durante la jornada pasa a ser falta leve. El techo de una falta leve es amonestación o dos días de suspensión de empleo y sueldo. Tienes diez días para sancionarla desde que te enteras. No puedes descontarla de la nómina. Y aplica a todos los establecimientos, tengas tres empleados o trescientos.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Barra de bar con un móvil boca abajo junto a un ticket y una caña a medias',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='por-fin' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.porFin'), '"Por fin algo que me deja decirle que suelte el móvil"')}
                            </h2>
                            <p className={article.p}>
                                {t(k('porFin.p1'), 'Lo has pensado esta semana. Nos ha pasado a todos.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porFin.p2'),
                                    'Antes era salir a fumar. Ahora es salir a fumar con el móvil, o directamente el móvil sin salir.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porFin.p3'),
                                    'Y la sensación es siempre la misma: ese rato no lo paga el cliente, lo pagas tú.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porFin.p4'),
                                    'Así que cuando salta el titular de que el BOE ya regula el móvil en hostelería, la lectura automática es: por fin tengo una herramienta.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('porFin.p5'),
                                        'La tienes. Es bastante más pequeña de lo que parece, y trae una trampa que escribes tú.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='boe'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.boe'), 'Lo que el BOE ha escrito, palabra por palabra')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('boe.p1.before'), 'La norma es el ')}
                                    <a
                                        href={REF.alehMod.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('boe.p1.link'),
                                            'acuerdo de modificación del VI Acuerdo Laboral Estatal para el sector de la Hostelería',
                                        )}
                                    </a>
                                    <SourceChip source='alehMod' />
                                </span>
                                {t(
                                    k('boe.p1.after'),
                                    ', el ALEH VI. Se firmó el 11 de junio de 2026 y se publicó en el BOE el 4 de septiembre de 2026.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('boe.p2'), 'Su vigencia llega hasta el 31 de diciembre de 2030.')}
                            </p>
                            <p className={article.p}>
                                {t(k('boe.p3.before'), 'Entre las faltas leves aparece un punto nuevo, el 12: ')}
                                <em>
                                    {t(
                                        k('boe.p3.quote'),
                                        '"Uso del teléfono móvil, redes sociales o dispositivos electrónicos durante la jornada laboral salvo autorización de la empresa o situación de emergencia"',
                                    )}
                                </em>
                                {t(k('boe.p3.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('boe.p4'),
                                    'Hay dos escalones más, y no son lo mismo. Aparecer en redes con el uniforme de la empresa, por iniciativa propia y sin su consentimiento previo, es falta grave.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('boe.p5.before'), 'Difamar a la empresa o a su marca en redes es muy grave, ')}
                                <em>
                                    {t(
                                        k('boe.p5.quote'),
                                        '"sin perjuicio del ejercicio de los derechos fundamentales"',
                                    )}
                                </em>
                                {t(k('boe.p5.after'), '.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('boe.p6.strong'),
                                        'El régimen de faltas y sanciones del ALEH se aplica a todo el sector, sin mínimo de plantilla.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('boe.p6.after'),
                                    'El límite de cincuenta trabajadores que están citando estos días varios medios corresponde al capítulo de medidas LGTBI del mismo acuerdo, no a las faltas.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='falta-leve'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.faltaLeve'), 'Falta leve significa dos días, y ni un euro')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('faltaLeve.p1.before'), 'El cuadro de sanciones del ALEH VI no ha cambiado')}
                                    <SourceChip source='alehVi' />
                                </span>
                                {t(
                                    k('faltaLeve.p1.after'),
                                    '. Una falta leve se salda con amonestación o con suspensión de empleo y sueldo de hasta dos días.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('faltaLeve.p2'),
                                    'Las graves van de tres a quince días. Las muy graves, de dieciséis a sesenta días o despido disciplinario.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('faltaLeve.p3.before'), 'Coger el móvil está en el primer escalón. ')}
                                <strong>
                                    {t(k('faltaLeve.p3.strong'), 'Amonestación o dos días. Ese es el techo.')}
                                </strong>
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('faltaLeve.p4.before'),
                                        'Y hay algo que en la barra se da por hecho y lleva cuarenta años prohibido: no puedes descontarlo del sueldo. El ',
                                    )}
                                    <a
                                        href={REF.et.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('faltaLeve.p4.link'), 'artículo 58.3 del Estatuto de los Trabajadores')}
                                    </a>
                                    <SourceChip source='et' />
                                </span>
                                {t(k('faltaLeve.p4.mid'), ' prohíbe ')}
                                <em>
                                    {t(
                                        k('faltaLeve.p4.quote'),
                                        '"sanciones que consistan en la reducción de la duración de las vacaciones u otra minoración de los derechos al descanso del trabajador o multa de haber"',
                                    )}
                                </em>
                                {t(k('faltaLeve.p4.after'), '.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('faltaLeve.p5'),
                                        'Ni multa, ni media hora menos, ni quitarle un día de fiesta.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='diez-dias'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.diezDias'), 'Diez días. Ese es todo el tiempo que tienes')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('diezDias.p1.strong'),
                                        'Una falta leve prescribe a los diez días desde que la empresa tuvo conocimiento de ella.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('diezDias.p1.after'),
                                    'Las graves, a los veinte. Las muy graves, a los sesenta. Y en todo caso a los seis meses de haberse cometido. Lo fija el artículo 60.2 del Estatuto de los Trabajadores y lo recoge igual el ALEH.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('diezDias.p2'),
                                    'Diez días naturales. En hostelería eso es un fin de semana largo y poco más.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('diezDias.p3'),
                                    'Si lo ves un viernes, lo comentas de pasada y lo sacas tres semanas después en una discusión por otra cosa, ya no vale nada.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='autorizacion'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.autorizacion'), '"Salvo autorización de la empresa": esa frase la escribes tú')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>{t(k('autorizacion.p1'), 'Aquí está la letra pequeña.')}</p>
                            <p className={article.p}>
                                {t(k('autorizacion.p2.before'), 'La falta no es usar el móvil. Es usarlo ')}
                                <strong>{t(k('autorizacion.p2.strong'), 'sin autorización de la empresa')}</strong>
                                {t(k('autorizacion.p2.after'), '. Y la empresa eres tú.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('autorizacion.p3'),
                                    'Piensa en tu último servicio. Los turnos se mandan por un grupo de WhatsApp.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('autorizacion.p4'),
                                    'Las reservas entran por Instagram y las coge quien esté libre. El repartidor avisa por teléfono. Puede que hasta las comandas vayan en el móvil.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('autorizacion.p5'),
                                    'Si trabajas así, has autorizado el móvil de hecho. Y esa es la primera alegación que va a poner encima de la mesa el trabajador.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('autorizacion.p6.strong'),
                                        'Sin una instrucción previa, escrita y comunicada a toda la plantilla, la sanción se cae.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('autorizacion.p6.after'),
                                    'Tiene que decir qué está autorizado, para quién, en qué momentos y con qué dispositivo.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('autorizacion.p7'),
                                    'Una vez escrita, es para todos. Si se la aplicas al del office y no al de barra, no tienes una norma, tienes un problema.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='coste'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.coste'), 'Lo que cuesta de verdad castigar a un camarero dos días')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('coste.disclaimer'),
                                        'Cálculo propio sobre el salario mínimo interprofesional de 2026, que es el suelo legal. Con salario de convenio real las cifras suben.',
                                    )}
                                </em>
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('coste.p1.before'), 'El ')}
                                    <a
                                        href={REF.smi.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('coste.p1.link'), 'SMI de 2026')}
                                    </a>
                                    <SourceChip source='smi' />
                                </span>
                                {t(
                                    k('coste.p1.after'),
                                    ' son 1.221 euros al mes en catorce pagas, 17.094 euros al año.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('coste.p2.before'), 'Eso son unos ')}
                                <strong>{t(k('coste.p2.day'), '47 euros de salario bruto al día')}</strong>
                                {t(k('coste.p2.mid'), '. Dos días de suspensión te dejan de coste algo menos de ')}
                                <strong>{t(k('coste.p2.twoDays'), '94 euros')}</strong>
                                {t(k('coste.p2.after'), ', más lo que te ahorras de cotización.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('coste.p3'),
                                    'Ahora la otra columna. Esos dos días hay alguien que no está en la barra un viernes y un sábado.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('coste.p4'),
                                    'O lo cubre un compañero y pagas horas que no tenías previstas, más caras que los 94 euros. O lo cubres tú, y son dos servicios enteros tuyos.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('coste.p5.strong'), 'Sancionas noventa euros y entregas dos turnos.')}
                                </strong>{' '}
                                {t(
                                    k('coste.p5.after'),
                                    'La suspensión de empleo y sueldo, en una plantilla ajustada, la acaba pagando la casa. Pasa lo mismo con casi todo el ',
                                )}
                                <BeneficioRealLink>
                                    {t(k('coste.p5.link'), 'beneficio real de un restaurante')}
                                </BeneficioRealLink>
                                {t(
                                    k('coste.p5.end'),
                                    ': el ahorro se ve y el coste de cubrirlo no aparece en ninguna parte.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('coste.p6'),
                                    'La amonestación por escrito no tiene ese problema, y es la que construye la reincidencia si algún día hay que subir de escalón.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cambio-gordo'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cambioGordo'), 'El cambio gordo no es el móvil')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('cambioGordo.p1'),
                                    'Mientras el titular hablaba del móvil, en el mismo acuerdo entraron dos cosas que te tocan más.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('cambioGordo.p2.strong'), 'El fichaje tiene ahora escalera propia.')}
                                </strong>{' '}
                                {t(
                                    k('cambioGordo.p2.after'),
                                    'Dos incumplimientos o mala praxis en el sistema de registro de jornada son falta leve. De tres a cuatro, falta grave. Cinco o más, muy grave.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('cambioGordo.p3.strong'),
                                        'Y antes de cualquier despido disciplinario hay audiencia previa.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('cambioGordo.p3.after'),
                                    'Tienes que comunicar al trabajador los hechos que le imputas y su calificación jurídica, y darle dos días para alegar antes de despedirlo. Mientras tanto sigue trabajando, o en permiso retribuido si le restringes el acceso.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('cambioGordo.p4'),
                                        'Esa es la que cambia cómo se despide en tu negocio. No el móvil.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='hoja'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.hoja'), 'La hoja que te falta, y en qué orden escribirla')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={policySteps} />
                        </div>
                    </section>

                    <section id='limites'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.limites'), 'Lo que el texto no cierra')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('limites.p1.strong'),
                                        '"Situación de emergencia" no está definida en ningún sitio.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('limites.p1.after'),
                                    'Una llamada del colegio a las dos y media, ¿entra? El acuerdo no lo dice. Hasta que haya sentencias, lo decide cada juzgado.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>{t(k('limites.p2.strong'), 'Las fechas no coinciden entre fuentes.')}</strong>{' '}
                                {t(
                                    k('limites.p2.after'),
                                    'El BOE fecha el acuerdo el 11 de junio de 2026 y lo publica el 4 de septiembre. Alguna publicación sectorial da como fecha de firma el 13 de abril de 2026. La buena es la del BOE.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>{t(k('limites.p3.strong'), 'Y está tu convenio provincial.')}</strong>{' '}
                                {t(
                                    k('limites.p3.after'),
                                    'El ALEH reserva el régimen disciplinario al ámbito estatal, así que esto manda sobre lo que diga tu provincia. Si tu convenio tiene su propia lista de faltas, pregúntale a tu gestoría cuál se aplica antes de sancionar a nadie. Nosotros contamos la norma, no la tramitamos.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('limites.p4'),
                                    'Si sale una sentencia que aclare lo de la emergencia, lo cuentas aquí antes que en ningún sitio.',
                                )}
                            </p>
                        </div>
                    </section>

                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('closing.p1'),
                                'Si no sabes cuánto te cuesta de verdad un día de personal, ni qué margen te deja cada servicio, una suspensión de dos días es un número a ciegas. Cuéntanoslo y lo miramos contigo.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('closing.p2'), 'Seguimos contando lo que a otros no les interesa contar.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sabes cuánto te cuesta de verdad un día de personal?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Revisamos contigo el coste real de la plantilla y lo que implica cubrir un turno que no estaba previsto.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>{sectorSources}</li>
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
